/* =====================================================================
 * ConditionalFormatDialog — 조건부 서식 룰빌더 다이얼로그(표현 컴포넌트)
 *
 * ExcelRenderer 의 DV 생성 다이얼로그와 같은 모달 톤을 유지하되, 파일 간
 * 순환 import(렌더러 ↔ 다이얼로그)를 피하기 위해 최소 모달 스타일을 자체
 * 보유한다(스타일 값은 ExcelRenderer 의 Modal* 컴포넌트와 동일 계열).
 *
 * 저작 범위(v1): cellIs(8연산자) / expression(수식) / colorScale(2·3색 프리셋).
 * dxf 는 배경색·글꼴색 스와치 + 굵게/기울임. 기존 규칙(선택 범위와 겹치는
 * 것) 목록과 규칙별 삭제 버튼을 함께 노출한다 — 제거 표면은 이 목록이다.
 *
 * 상태는 다이얼로그 내부에서만 관리(props in / callbacks out). 조건부 렌더
 * (`cfDialogOpen && <ConditionalFormatDialog/>`)로 열릴 때마다 초기화된다.
 *
 * data-cf-* 속성은 브라우저 하네스(verify-cf.mjs)의 안정 셀렉터 계약이다
 * (오토필터의 data-filter-btn 선례) — 임의로 제거하지 말 것.
 * ===================================================================== */

import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { X, Trash2 } from 'lucide-react';

/* ------------------------------------------------------------------ */
/* 공개 타입 (ExcelRenderer 가 소비)                                    */
/* ------------------------------------------------------------------ */

/** cellIs 비교 연산자 — IR/패치 op 의 v1 enum 과 1:1. */
export type CfDialogOperator =
  | 'greaterThan' | 'greaterThanOrEqual' | 'lessThan' | 'lessThanOrEqual'
  | 'equal' | 'notEqual' | 'between' | 'notBetween';

/** 적용 콜백 페이로드 — 렌더러가 CfRuleParsed 로 변환해 cfBlocks 에 추가한다. */
export interface CfDialogRuleDraft {
  type: 'cellIs' | 'expression' | 'colorScale';
  /** cellIs 전용. */
  operator?: CfDialogOperator;
  /** cellIs: 1~2개, expression: 1개(선행 '=' 제거됨), colorScale: []. */
  formulas: string[];
  /** 차등 서식(cellIs/expression 전용). */
  dxf?: { bgColor?: string; fontColor?: string; bold?: boolean; italic?: boolean };
  /** 색조(colorScale 전용). */
  colorScale?: { cfvo: Array<{ type: string; val?: number }>; colors: string[] };
}

/** 선택 범위와 겹치는 기존 규칙 1건(삭제 표면). 인덱스는 cfBlocks 좌표. */
export interface CfExistingRuleItem {
  blockIdx: number;
  ruleIdx: number;
  /** 규칙이 걸린 범위(A1, 공백 구분 복수 가능). */
  rangeA1: string;
  /** 사람이 읽는 한 줄 요약(렌더러가 생성). */
  summary: string;
  /** 미리보기 스와치 색 목록(dxf 배경/글꼴색 또는 colorScale colors). */
  swatches: string[];
}

interface ConditionalFormatDialogProps {
  /** 현재 선택 범위 A1(표시용). */
  selectionA1: string;
  /** 선택 범위와 겹치는 기존 규칙 목록. */
  existingRules: CfExistingRuleItem[];
  onApply: (draft: CfDialogRuleDraft) => void;
  onRemove: (blockIdx: number, ruleIdx: number) => void;
  onClose: () => void;
}

/* ------------------------------------------------------------------ */
/* 프리셋                                                               */
/* ------------------------------------------------------------------ */

const OPERATORS: Array<{ value: CfDialogOperator; label: string }> = [
  { value: 'greaterThan', label: '보다 큼 (>)' },
  { value: 'greaterThanOrEqual', label: '이상 (≥)' },
  { value: 'lessThan', label: '보다 작음 (<)' },
  { value: 'lessThanOrEqual', label: '이하 (≤)' },
  { value: 'equal', label: '같음 (=)' },
  { value: 'notEqual', label: '같지 않음 (≠)' },
  { value: 'between', label: '사이' },
  { value: 'notBetween', label: '사이 아님' },
];

/** 배경색 스와치 — Excel 조건부 서식 기본 팔레트 계열. */
const BG_SWATCHES = ['#FFC7CE', '#FFEB9C', '#C6EFCE', '#BDD7EE', '#FCE4D6', '#FF0000', '#FFFF00', '#00B050'];
/** 글꼴색 스와치. */
const FONT_SWATCHES = ['#9C0006', '#9C6500', '#006100', '#1F4E79', '#FF0000', '#0070C0', '#000000'];

/** colorScale 프리셋 — colors 는 cfvo(min→max) 순. */
const SCALE_PRESETS: Array<{ label: string; colors: string[] }> = [
  { label: '흰색 → 초록', colors: ['#FFFFFF', '#63BE7B'] },
  { label: '흰색 → 빨강', colors: ['#FFFFFF', '#F8696B'] },
  { label: '흰색 → 파랑', colors: ['#FFFFFF', '#5A8AC6'] },
  { label: '빨강 → 노랑 → 초록', colors: ['#F8696B', '#FFEB84', '#63BE7B'] },
  { label: '초록 → 노랑 → 빨강', colors: ['#63BE7B', '#FFEB84', '#F8696B'] },
  { label: '빨강 → 흰색 → 파랑', colors: ['#F8696B', '#FFFFFF', '#5A8AC6'] },
];

/** 프리셋 colors → cfvo(2색 min/max, 3색 min/50퍼센타일/max). */
const cfvoForColors = (colors: string[]): Array<{ type: string; val?: number }> =>
  colors.length === 3
    ? [{ type: 'min' }, { type: 'percentile', val: 50 }, { type: 'max' }]
    : [{ type: 'min' }, { type: 'max' }];

/* ------------------------------------------------------------------ */
/* 컴포넌트                                                             */
/* ------------------------------------------------------------------ */

export const ConditionalFormatDialog: React.FC<ConditionalFormatDialogProps> = ({
  selectionA1,
  existingRules,
  onApply,
  onRemove,
  onClose,
}) => {
  const [ruleType, setRuleType] = useState<CfDialogRuleDraft['type']>('cellIs');
  const [operator, setOperator] = useState<CfDialogOperator>('greaterThan');
  const [value1, setValue1] = useState('');
  const [value2, setValue2] = useState('');
  const [formula, setFormula] = useState('');
  const [bgColor, setBgColor] = useState('');
  const [fontColor, setFontColor] = useState('');
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [scaleIdx, setScaleIdx] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const needsValue2 = operator === 'between' || operator === 'notBetween';

  /** 선행 '=' 제거(IR 규약: 수식은 '=' 없이 저장). */
  const stripEq = (s: string): string => {
    const t = s.trim();
    return t.startsWith('=') ? t.slice(1) : t;
  };

  const handleApply = () => {
    if (ruleType === 'colorScale') {
      const preset = SCALE_PRESETS[scaleIdx] ?? SCALE_PRESETS[0];
      onApply({
        type: 'colorScale',
        formulas: [],
        colorScale: { cfvo: cfvoForColors(preset.colors), colors: [...preset.colors] },
      });
      return;
    }
    /* cellIs / expression — dxf 최소 1속성 필수 */
    const dxf: NonNullable<CfDialogRuleDraft['dxf']> = {};
    if (bgColor) dxf.bgColor = bgColor;
    if (fontColor) dxf.fontColor = fontColor;
    if (bold) dxf.bold = true;
    if (italic) dxf.italic = true;
    if (Object.keys(dxf).length === 0) {
      setError('적용할 서식(배경색·글꼴색·굵게·기울임)을 하나 이상 선택하세요.');
      return;
    }
    if (ruleType === 'cellIs') {
      const v1 = stripEq(value1);
      if (!v1) {
        setError('비교할 값을 입력하세요.');
        return;
      }
      const formulas = [v1];
      if (needsValue2) {
        const v2 = stripEq(value2);
        if (!v2) {
          setError('두 번째 값을 입력하세요.');
          return;
        }
        formulas.push(v2);
      }
      onApply({ type: 'cellIs', operator, formulas, dxf });
      return;
    }
    const fx = stripEq(formula);
    if (!fx) {
      setError('조건 수식을 입력하세요.');
      return;
    }
    onApply({ type: 'expression', formulas: [fx], dxf });
  };

  return (
    <Overlay onClick={onClose}>
      <Box data-cf-dialog="1" onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>조건부 서식 {selectionA1 ? `— ${selectionA1}` : ''}</Title>
          <CloseBtn onClick={onClose} aria-label="닫기">
            <X />
          </CloseBtn>
        </Header>
        <Body>
          {/* 규칙 유형 */}
          <Label>규칙 유형</Label>
          <SegmentRow>
            <SegmentBtn
              data-cf-type="cellIs"
              $active={ruleType === 'cellIs'}
              onClick={() => { setRuleType('cellIs'); setError(null); }}
            >
              셀 값 조건
            </SegmentBtn>
            <SegmentBtn
              data-cf-type="expression"
              $active={ruleType === 'expression'}
              onClick={() => { setRuleType('expression'); setError(null); }}
            >
              수식
            </SegmentBtn>
            <SegmentBtn
              data-cf-type="colorScale"
              $active={ruleType === 'colorScale'}
              onClick={() => { setRuleType('colorScale'); setError(null); }}
            >
              색조
            </SegmentBtn>
          </SegmentRow>

          {ruleType === 'cellIs' && (
            <>
              <Label>조건</Label>
              <CondRow>
                <Select
                  data-cf-operator="1"
                  value={operator}
                  onChange={(e) => { setOperator(e.target.value as CfDialogOperator); setError(null); }}
                >
                  {OPERATORS.map((op) => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </Select>
                <Input
                  data-cf-value1="1"
                  value={value1}
                  placeholder="값 (예: 0)"
                  onChange={(e) => { setValue1(e.target.value); setError(null); }}
                />
                {needsValue2 && (
                  <Input
                    data-cf-value2="1"
                    value={value2}
                    placeholder="값 2"
                    onChange={(e) => { setValue2(e.target.value); setError(null); }}
                  />
                )}
              </CondRow>
            </>
          )}

          {ruleType === 'expression' && (
            <>
              <Label>조건 수식 (TRUE 인 셀에 서식 적용)</Label>
              <Input
                data-cf-formula="1"
                value={formula}
                placeholder="=B2>AVERAGE($B$2:$B$9)"
                onChange={(e) => { setFormula(e.target.value); setError(null); }}
              />
              <Hint>범위 좌상단 셀 기준 상대 참조로 평가됩니다(Excel 동일).</Hint>
            </>
          )}

          {(ruleType === 'cellIs' || ruleType === 'expression') && (
            <>
              <Label>배경색</Label>
              <SwatchRow>
                <NoneBtn data-cf-bg-none="1" $active={bgColor === ''} onClick={() => { setBgColor(''); setError(null); }}>
                  없음
                </NoneBtn>
                {BG_SWATCHES.map((c) => (
                  <Swatch
                    key={c}
                    data-cf-bg={c}
                    $color={c}
                    $active={bgColor === c}
                    aria-label={`배경색 ${c}`}
                    onClick={() => { setBgColor(c); setError(null); }}
                  />
                ))}
              </SwatchRow>
              <Label>글꼴색</Label>
              <SwatchRow>
                <NoneBtn data-cf-font-none="1" $active={fontColor === ''} onClick={() => { setFontColor(''); setError(null); }}>
                  없음
                </NoneBtn>
                {FONT_SWATCHES.map((c) => (
                  <Swatch
                    key={c}
                    data-cf-font={c}
                    $color={c}
                    $active={fontColor === c}
                    aria-label={`글꼴색 ${c}`}
                    onClick={() => { setFontColor(c); setError(null); }}
                  />
                ))}
              </SwatchRow>
              <StyleRow>
                <StyleToggle data-cf-bold="1" $active={bold} onClick={() => { setBold((v) => !v); setError(null); }}>
                  <b>가</b> 굵게
                </StyleToggle>
                <StyleToggle data-cf-italic="1" $active={italic} onClick={() => { setItalic((v) => !v); setError(null); }}>
                  <i>가</i> 기울임
                </StyleToggle>
              </StyleRow>
            </>
          )}

          {ruleType === 'colorScale' && (
            <>
              <Label>색조 프리셋 (값이 작음 → 큼)</Label>
              <PresetCol>
                {SCALE_PRESETS.map((p, i) => (
                  <PresetBtn
                    key={p.label}
                    data-cf-scale={i}
                    $active={scaleIdx === i}
                    onClick={() => { setScaleIdx(i); setError(null); }}
                  >
                    <PresetChip style={{ background: `linear-gradient(to right, ${p.colors.join(', ')})` }} />
                    <span>{p.label}</span>
                  </PresetBtn>
                ))}
              </PresetCol>
            </>
          )}

          {error && <ErrorText data-cf-error="1">{error}</ErrorText>}

          {/* 기존 규칙(선택 범위와 겹치는 것) */}
          {existingRules.length > 0 && (
            <>
              <Divider />
              <Label>이 범위의 기존 규칙</Label>
              <RuleList>
                {existingRules.map((r) => (
                  <RuleItem key={`${r.blockIdx}:${r.ruleIdx}`}>
                    <RuleSwatches>
                      {r.swatches.slice(0, 3).map((c, i) => (
                        <RuleSwatch key={`${c}:${i}`} $color={c} />
                      ))}
                    </RuleSwatches>
                    <RuleText>
                      <RuleSummary>{r.summary}</RuleSummary>
                      <RuleRange>{r.rangeA1}</RuleRange>
                    </RuleText>
                    <RuleDelBtn
                      data-cf-rule-del={`${r.blockIdx}:${r.ruleIdx}`}
                      aria-label="규칙 삭제"
                      onClick={() => onRemove(r.blockIdx, r.ruleIdx)}
                    >
                      <Trash2 />
                    </RuleDelBtn>
                  </RuleItem>
                ))}
              </RuleList>
            </>
          )}
        </Body>
        <Footer>
          <FooterBtn onClick={onClose}>취소</FooterBtn>
          <FooterBtn data-cf-apply="1" $primary onClick={handleApply}>
            적용
          </FooterBtn>
        </Footer>
      </Box>
    </Overlay>
  );
};

/* ------------------------------------------------------------------ */
/* 스타일 — ExcelRenderer Modal* 과 동일 계열(순환 import 회피용 사본)   */
/* ------------------------------------------------------------------ */

/** ExcelRenderer 의 EXCEL_GREEN 과 동일 강조색. */
const ACCENT = '#217346';
const ACCENT_DARK = '#185C37';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.bgOverlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
`;

const Box = styled.div`
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  width: 400px;
  max-width: 90vw;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
`;

const Title = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CloseBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Body = styled.div`
  padding: 16px;
  overflow-y: auto;
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 12px 0 6px;

  &:first-child {
    margin-top: 0;
  }
`;

const SegmentRow = styled.div`
  display: flex;
  gap: 4px;
`;

const SegmentBtn = styled.button<{ $active?: boolean }>`
  flex: 1;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $active }) =>
    $active &&
    css`
      border-color: ${ACCENT};
      background: rgba(33, 115, 70, 0.08);
      color: ${ACCENT};
    `}

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const CondRow = styled.div`
  display: flex;
  gap: 6px;
`;

const Select = styled.select`
  flex: 1.2;
  min-width: 0;
  padding: 8px 8px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  outline: none;

  &:focus {
    border-color: ${ACCENT};
  }
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: 8px 12px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &:focus {
    border-color: ${ACCENT};
  }
`;

const Hint = styled.p`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 6px 0 0;
  line-height: 1.5;
`;

const SwatchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const Swatch = styled.button<{ $color: string; $active?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: ${({ $color }) => $color};
  border: 2px solid ${({ $active, theme }) => ($active ? ACCENT : theme.colors.borderLight)};
  transition: transform ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: scale(1.12);
  }
`;

const NoneBtn = styled.button<{ $active?: boolean }>`
  height: 22px;
  padding: 0 8px;
  border-radius: 6px;
  font-size: 0.6875rem;
  border: 1px solid ${({ $active, theme }) => ($active ? ACCENT : theme.colors.borderLight)};
  color: ${({ $active, theme }) => ($active ? ACCENT : theme.colors.textMuted)};
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const StyleRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 12px;
`;

const StyleToggle = styled.button<{ $active?: boolean }>`
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid ${({ $active, theme }) => ($active ? ACCENT : theme.colors.borderLight)};
  background: ${({ $active, theme }) => ($active ? 'rgba(33, 115, 70, 0.08)' : theme.colors.bgSecondary)};
  color: ${({ $active, theme }) => ($active ? ACCENT : theme.colors.textSecondary)};
  transition: all ${({ theme }) => theme.transitions.fast};
`;

const PresetCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PresetBtn = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 0.75rem;
  text-align: left;
  border: 1px solid ${({ $active, theme }) => ($active ? ACCENT : theme.colors.borderLight)};
  background: ${({ $active, theme }) => ($active ? 'rgba(33, 115, 70, 0.08)' : theme.colors.bgSecondary)};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all ${({ theme }) => theme.transitions.fast};
`;

const PresetChip = styled.span`
  width: 72px;
  height: 14px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
`;

const ErrorText = styled.span`
  display: block;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.error};
  margin-top: 8px;
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.borderLight};
  margin: 14px 0 2px;
`;

const RuleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 150px;
  overflow-y: auto;
`;

const RuleItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const RuleSwatches = styled.div`
  display: flex;
  gap: 2px;
  flex-shrink: 0;
`;

const RuleSwatch = styled.span<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const RuleText = styled.div`
  flex: 1;
  min-width: 0;
`;

const RuleSummary = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RuleRange = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const RuleDelBtn = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.error};
  }

  svg {
    width: 13px;
    height: 13px;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 16px 14px;
  flex-shrink: 0;
`;

const FooterBtn = styled.button<{ $primary?: boolean }>`
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $primary, theme }) =>
    $primary
      ? css`
          background: ${ACCENT};
          color: #ffffff;
          &:hover:not(:disabled) {
            background: ${ACCENT_DARK};
          }
        `
      : css`
          background: ${theme.colors.bgSecondary};
          color: ${theme.colors.textSecondary};
          border: 1px solid ${theme.colors.borderLight};
          &:hover:not(:disabled) {
            background: ${theme.colors.bgHover};
            color: ${theme.colors.textPrimary};
          }
        `}
`;

export default ConditionalFormatDialog;
